import type { SupplierProductGetResponseChildType } from "@sellar-io/api-v2/types";
import { Switch } from "@sellar-io/components";
import { forwardRef, useImperativeHandle, useState } from "react";
import { Alert } from "../../../../components/alert/Alert";
import { useAvailabilityTierList } from "../../../../hooks/api/v2/availabilityTier/useAvailabilityTierList";
import { useProductAvailabilityTierPatch } from "../../../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierPatch";
import { useProductStatusUpdate } from "../../../../hooks/api/v2/product/useProductStatusUpdate";
import type { ProductItemAvailabilitySectionHandle } from "./ProductItemAvailabilitySection";

export const BasicProductItemAvailabilitySection = forwardRef<
  ProductItemAvailabilitySectionHandle,
  { product: SupplierProductGetResponseChildType }
>(({ product }, ref) => {
  const [hiddenOverride, setHiddenOverride] = useState<boolean | null>(null);
  const productStatusUpdateMutation = useProductStatusUpdate(product.id);
  const productAvailabilityTierPatchMutation =
    useProductAvailabilityTierPatch();
  const availabilityTierListQuery = useAvailabilityTierList({
    limit: 50,
    query: [{ type: "ProductId", modifier: "EQUALS", value: product.id }],
  });
  const defaultAvailabilityTier = availabilityTierListQuery.data?.pages
    .flat()
    .find((tier) => tier.defaultType === "default");
  const defaultProductAvailabilityTier =
    defaultAvailabilityTier?.ProductAvailabilityTiers[0];
  const serverHidden =
    !product.active || defaultProductAvailabilityTier?.active === false;
  const isHidden = hiddenOverride ?? serverHidden;
  const shouldUpdate =
    isHidden !== serverHidden || (!isHidden && !defaultProductAvailabilityTier);
  const save = async () => {
    if (!shouldUpdate || !defaultAvailabilityTier) {
      return;
    }
    const active = !isHidden;
    if (defaultProductAvailabilityTier) {
      await productAvailabilityTierPatchMutation.mutateAsync([
        {
          id: defaultProductAvailabilityTier.id,
          value: { id: defaultProductAvailabilityTier.id, active },
          action: "PUT",
        },
      ]);
    } else if (active) {
      await productAvailabilityTierPatchMutation.mutateAsync([
        {
          value: {
            ProductId: product.id,
            AvailabilityTierId: defaultAvailabilityTier.id,
            active: true,
          },
          action: "POST",
        },
      ]);
    }
    if (active && !product.active) {
      await productStatusUpdateMutation.mutateAsync({ active: true });
    }
  };
  useImperativeHandle(ref, () => ({ save }));
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Switch checked={isHidden} onChange={setHiddenOverride} />
        <span className="text-zinc-700">Hide product from customers</span>
      </div>
      {isHidden && (
        <Alert
          title="This product item is hidden"
          message="Toggle off the switch to make this item visible to customers again."
        />
      )}
    </div>
  );
});

BasicProductItemAvailabilitySection.displayName =
  "BasicProductItemAvailabilitySection";
